import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { seedDatabase } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Package, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedDatabase();
      toast.success('Demo data loaded! Try: admin@constructhire.com / admin123');
    } catch (error) {
      if (error.response?.data?.message === 'Database already seeded') {
        toast.info('Demo data already loaded');
      } else {
        toast.error('Failed to load demo data');
      }
    } finally {
      setSeeding(false);
    }
  };

  const fillDemoCredentials = (role) => {
    const credentials = {
      admin: { email: 'admin@constructhire.com', password: 'admin123' },
      manager: { email: 'manager@constructhire.com', password: 'manager123' },
      driver: { email: 'driver@constructhire.com', password: 'driver123' },
    };
    setEmail(credentials[role].email);
    setPassword(credentials[role].password);
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-secondary relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1590834367872-3297c46273ac?w=1200)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 flex flex-col justify-center px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-primary rounded flex items-center justify-center">
              <Package className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-heading font-black text-4xl text-white tracking-tight">
              CONSTRUCTHIRE
            </h1>
          </div>
          <h2 className="font-heading font-bold text-2xl text-white mb-4">
            ASSET TRACKING SYSTEM
          </h2>
          <p className="font-body text-slate-300 text-lg max-w-md">
            Track your construction equipment with barcode scanning. 
            Manage hire orders, report damage, and keep your fleet running smoothly.
          </p>
          
          <div className="mt-12 grid grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur p-4 rounded border border-white/20">
              <div className="font-heading font-black text-3xl text-primary">500+</div>
              <div className="font-mono text-xs text-slate-300 uppercase tracking-wider">Assets Tracked</div>
            </div>
            <div className="bg-white/10 backdrop-blur p-4 rounded border border-white/20">
              <div className="font-heading font-black text-3xl text-primary">98%</div>
              <div className="font-mono text-xs text-slate-300 uppercase tracking-wider">Uptime</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-12 h-12 bg-primary rounded flex items-center justify-center">
              <Package className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading font-black text-2xl text-secondary">
              CONSTRUCTHIRE
            </span>
          </div>

          <div className="bg-white border-2 border-slate-200 shadow-industrial p-8">
            <h2 className="font-heading font-black text-2xl text-slate-900 uppercase mb-2">
              Sign In
            </h2>
            <p className="font-body text-slate-500 mb-6">
              Enter your credentials to access your account
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="font-heading font-bold uppercase text-xs tracking-wider">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="mt-1 h-12 border-2 border-slate-200 focus:border-primary"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="font-heading font-bold uppercase text-xs tracking-wider">
                  Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="h-12 border-2 border-slate-200 focus:border-primary pr-12"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-primary hover:bg-primary-hover text-white font-heading font-bold uppercase tracking-wide shadow-industrial-sm active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
                data-testid="submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <p className="mt-6 text-center font-body text-sm text-slate-500">
              Contact your administrator for account access
            </p>

            {/* Demo Credentials Section */}
            <div className="mt-8 pt-6 border-t-2 border-slate-200">
              <p className="font-mono text-xs text-slate-400 uppercase tracking-wider mb-3 text-center">
                Demo Accounts
              </p>
              <div className="flex gap-2 justify-center flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fillDemoCredentials('admin')}
                  className="text-xs"
                  data-testid="demo-admin-btn"
                >
                  Admin
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fillDemoCredentials('manager')}
                  className="text-xs"
                  data-testid="demo-manager-btn"
                >
                  Manager
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fillDemoCredentials('driver')}
                  className="text-xs"
                  data-testid="demo-driver-btn"
                >
                  Driver
                </Button>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSeedData}
                disabled={seeding}
                className="w-full mt-3 text-xs text-slate-500"
                data-testid="seed-data-btn"
              >
                {seeding ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin mr-2" />
                    Loading Demo Data...
                  </>
                ) : (
                  'Load Demo Data'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
